import { Component, FC, useCallback, memo } from 'react'
import { Field, reduxForm, change } from 'redux-form'
import { parseValueToBoolean } from 'helpers/formHelpers'
import { FORM_RECAT_TRANSACTION } from 'constants/forms'
import { ReduxFormProps, StoreDispatch } from 'interfaces/component'
import { Category, CategoryId, MetaData } from 'interfaces/meta'
import { getItemsFromCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { State } from 'interfaces/state'
import CheckboxWrapped from 'components/atoms/inputs/CheckboxWrapped'
import styles from 'components/features/pfm/TransactionRecatForm/TransactionRecatForm.styl'
import paneStyles from 'components/features/pfm/GenericFloatingPane/FloatingPaneOldStyles.styl'
import Button from 'components/features/pfm/Button/Button'
import { TransactionRecatFormValues } from 'interfaces/form'
import { TransactionCategoryOptionWrapped } from 'components/features/pfm/TransactionCategory/TransactionCategoryOption'
import { connect } from 'react-redux'
import { modifyFloatingPaneActionCreator, killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { TransactionRecatFloatingPane } from 'interfaces/floatingPanes'
import { isFetchableLoaded } from 'helpers/loadableHelpers'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { TransactionPayload, changeTransactionCategoryActionCreator } from 'actions/moneyflowActions'
import { saturateCategory } from 'helpers/categoriesHelpers'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { TransactionNarrow } from 'components/features/pfm/TransactionNarrow/TransactionNarrow'
import { defaultMemoize } from 'reselect'
import { MoneyflowType, TransactionUid } from 'interfaces/moneyflow'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'


type ApplyToSimilarCheckboxProps =
  & Pick<TransactionRecatFloatingPane, 'selectedTransactionUids' | 'similarTransactions'>
  & {
    transactionUid: TransactionUid
    onChange: (value: boolean) => void
  }

const ApplyToSimilarCheckbox: FC<ApplyToSimilarCheckboxProps> = (props) => {
  const { selectedTransactionUids, similarTransactions, onChange } = props

  const dispatch = useStoreDispatch()
  const handleLinkClick = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('transaction-recat', { mode: 'similar-transactions' }))
  }, [dispatch])

  const linkChild = (
    <a
      className={ styles.transactionsLink }
      onClick={ handleLinkClick }
    >
      See Transactions
    </a>
  )

  if (!isFetchableLoaded(similarTransactions) || !similarTransactions.data.allKeys.length) {
    return null
  }

  const count = selectedTransactionUids && selectedTransactionUids.length
    ? selectedTransactionUids.length
    : similarTransactions.data.allKeys.length

  if (count === 0) {
    return null
  }

  const APPLY_RECAT_TO_ALL_PROPS = {
    label: <span>Apply to <strong>{ count }</strong> matching transactions.</span>,
    qaMarker: 'apply-to-all',
  }

  return (
    <div className={ styles.applyToAll }>
      <Field
        name='apply_to_all'
        props={ APPLY_RECAT_TO_ALL_PROPS }
        component={ CheckboxWrapped }
        parse={ parseValueToBoolean }
        onChange={ (_e: any, nextValue: boolean) => {
          onChange(nextValue)
        } }
      />
      { linkChild }
    </div>
  )
}

type CategoryGroupProps = {
  title: string
  type: MoneyflowType | 'transfer'
  categories: Category[]
  hideLabel?: boolean
}

const CategoryGroup: FC<CategoryGroupProps> = memo((props) => {
  const { title, categories, type, hideLabel } = props

  if (!categories.length) {
    return null
  }

  const transferInfoChild = (
    <li className={ styles.transferInfo }>
      <small>
        Transfers are excluded from spending and income. Examples include credit card payments and stock trades.
      </small>
    </li>
  )

  return (
    <fieldset className={ styles.categoryGroup }>
      { !hideLabel && <legend className={ styles.categoryGroupTitle }>{ title }</legend> }
      <ul className={ styles.categories }>
        {
          categories.map((category: Category) => (
            <Field
              component={ TransactionCategoryOptionWrapped }
              category={{ ...category, type }}
              name='categoryId'
              key={ category.id }
              value={ category.id }
              type='radio'
            />
          ))
        }
        { type === 'transfer' && transferInfoChild }
      </ul>
    </fieldset>
  )
})

type TransactionRecatFormStateProps =
  & ReduxFormProps
  & {
    incomeCategories: Category[]
    spendingCategories: Category[]
    transferCategories: Category[]
  }

type TransactionRecatFormDispatchProps =
  & Pick<ReduxFormProps, 'onSubmit'>
  & {
    closePane: () => void
    onApplyToAllChange: (nextValue: boolean) => void
    changeCreateRuleField: (nextValue: boolean) => void
  }

type PropKeys = 'transaction' | 'selectedTransactionUids' | 'similarTransactions' | 'createRuleWasAutochecked'
export type TransactionRecatFormOwnProps =
  & Pick<TransactionRecatFloatingPane, PropKeys>
  & {
    meta: MetaData | null
    onClose: () => void
  }

type TransactionRecatFormProps =
  & TransactionRecatFormStateProps
  & TransactionRecatFormDispatchProps
  & TransactionRecatFormOwnProps

@reduxForm({
  form: FORM_RECAT_TRANSACTION,
  destroyOnUnmount: false,
})
class TransactionRecatFormComponent extends Component<TransactionRecatFormProps> {
  render () {
    const {
      spendingCategories,
      incomeCategories,
      transferCategories,
      transaction,
      handleSubmit,
      selectedTransactionUids,
      similarTransactions,
      closePane,
    } = this.props

    if (!transaction) {
      return null
    }

    const header = (
      <div className={ styles.headerWrapper }>
        <h2 className={ paneStyles.title }>Select a category for</h2>
        <TransactionNarrow transaction={ transaction } hideEdit hideCategory hideLongDescription />
      </div>
    )

    const footer = (
      <>
        <fieldset className={ styles.checkboxes }>
          <ApplyToSimilarCheckbox
            selectedTransactionUids={ selectedTransactionUids }
            similarTransactions={ similarTransactions }
            transactionUid={ transaction.uid }
            onChange={ this.handleApplyToAllChange }
          />
          <Field
            name='create_rule'
            props={{
              label: 'Remember and apply to future transactions.',
              qaMarker: 'create-rule',
              onValueChange: () => null,
            }}
            component={ CheckboxWrapped }
            parse={ parseValueToBoolean }
          />
        </fieldset>
        <Button
          size='medium'
          text={ selectedTransactionUids.length > 1 ? 'Save Transactions' : 'Save Transaction' }
          qaMarker='save-button'
          fullWidth
          submit
        />
      </>
    )

    const isFromRailsbank = transaction.account_source_type === 'railsbank'

    const statusCardNote = isFromRailsbank && (
      <>
        <FloatingPaneNotification
          theme='info'
          style='textOnly'
          text='Status Card transactions can only be categorized as spending.'
          iconSlug='info'
          iconCircled
          small
        />
        <br />
      </>
    )

    return (
      <form onSubmit={ handleSubmit }>
        <FloatingPaneLayout
          type='transaction-recat'
          header={ header }
          footer={ footer }
          bodyClassName={ styles.body }
          onClose={ closePane }
        >
          { statusCardNote }
          { !isFromRailsbank && <CategoryGroup title='Income' type='income' categories={ incomeCategories } /> }
          <CategoryGroup title='Spending' type='spending' categories={ spendingCategories } hideLabel={ isFromRailsbank } />
          { !isFromRailsbank && <CategoryGroup title='Transfers' type='transfer' categories={ transferCategories } /> }
        </FloatingPaneLayout>
      </form>
    )
  }

  handleApplyToAllChange = (nextValue: boolean) => {
    const {
      createRuleWasAutochecked,
      changeCreateRuleField,
      onApplyToAllChange,
    } = this.props

    onApplyToAllChange(nextValue)

    if (!createRuleWasAutochecked) {
      changeCreateRuleField(true)
    }
  }
}

const OMITTED_CATEGORIES: CategoryId[] = ['transfer', 'all']

const constructStateProps = defaultMemoize((categoryId: CategoryId, meta: MetaData): TransactionRecatFormStateProps => {
  const initialValues: TransactionRecatFormValues = {
    categoryId,
    apply_to_all: false,
    create_rule: false,
  }

  const transferCategory = getItemFromCollection<Category, 'id'>(meta.spending_categories, 'transfer')

  return {
    incomeCategories: getItemsFromCollection<Category, 'id'>(meta.income_categories).filter((category) => !OMITTED_CATEGORIES.includes(category.id)),
    spendingCategories: getItemsFromCollection<Category, 'id'>(meta.spending_categories).filter((category) => !OMITTED_CATEGORIES.includes(category.id)),
    transferCategories: transferCategory ? [transferCategory] : [],
    initialValues,
  }
})

const mapStateToProps = (state: State, ownProps: TransactionRecatFormOwnProps): TransactionRecatFormStateProps => {
  const { transaction } = ownProps
  const categoryId = transaction ? transaction.category.id : ''
  return constructStateProps(categoryId, state.data.meta)
}

const mapDispatchToProps = (dispatch: StoreDispatch, ownProps: TransactionRecatFormOwnProps): TransactionRecatFormDispatchProps => {
  const { transaction, similarTransactions, meta, selectedTransactionUids, onClose } = ownProps

  return {
    onSubmit: (values: TransactionRecatFormValues) => {
      if (!meta || !transaction) {
        return {}
      }
      const saturatedCategory = saturateCategory(values.categoryId, meta)
      if (!saturatedCategory) {
        return {}
      }

      const { create_rule } = values

      const payload: TransactionPayload = {
        transaction,
        type: null,
        category: saturatedCategory,
        create_rule,
        apply_to_ids: selectedTransactionUids,
      }

      dispatch(changeTransactionCategoryActionCreator.request(payload))
      return {}
    },
    closePane: () => {
      onClose()
      dispatch(killFloatingPanesActionCreator('transaction-recat'))
    },
    onApplyToAllChange: (nextValue: boolean) => {
      const ids = nextValue && isFetchableLoaded(similarTransactions) ? similarTransactions.data.allKeys : []
      dispatch(modifyFloatingPaneActionCreator('transaction-recat', { selectedTransactionUids: [...ids], createRuleWasAutochecked: true }))
    },
    changeCreateRuleField: (nextValue: boolean) => {
      dispatch(change(FORM_RECAT_TRANSACTION, 'create_rule', nextValue))
    },
  }
}

export const TransactionRecatForm = connect(mapStateToProps, mapDispatchToProps)(TransactionRecatFormComponent)
